/**
 * historyAggregator.js
 * Historical Analytics Layer — public entry point.
 *
 * This is the ONLY file Dashboard, Executive Report, and any future
 * Analytics consumer should ever import from this layer. It composes
 * transactions.js and releases.js into one unified result shape and
 * computes delta/deltaPct — the one calculation genuinely owned by
 * this layer itself (everything else is pure consumption).
 *
 * Like its two providers, this file is STATELESS with respect to "what
 * is the current date". It takes a fully-formed HistoricalContext from
 * the caller and does nothing with today's date. Building that context
 * is the caller's responsibility, deliberately kept out of this layer.
 *
 * HistoricalContext (not a flat calendar Period) exists because this
 * layer has two structurally different comparison bases baked in from
 * the start — Transaction-based (a calendar date boundary genuinely
 * determines what's included) and Release-based (the comparison point
 * is a persisted forecast_date on project_forecast_releases, which is
 * NOT guaranteed to be a month-end date — it is whatever date a Cost
 * Control Engineer assigned when the Revision was committed). A flat
 * {currentPeriodEnd, previousPeriodEnd} shape hid that distinction and
 * invited the false assumption that both providers interpret the same
 * date the same way. The `mode` field makes the comparison basis
 * explicit in the contract itself, and leaves room for future modes
 * (e.g. release-to-release by release_number, arbitrary custom ranges)
 * without changing this public API.
 */
import { getTransactionHistory } from './transactions'
import { getReleaseHistory } from './releases'

/**
 * @typedef {Object} HistoricalContext
 * @property {'calendar'} mode - comparison mode. Only 'calendar' is
 *   implemented today (calendar-boundary comparison, calculated by the
 *   caller per the "always compare to the last day of the previous
 *   month" rule). Additional modes (e.g. 'release', 'custom_range') can
 *   be added later without breaking this shape — each would populate
 *   its own named sub-object instead of `calendar`.
 * @property {string} label - human-readable, e.g. "August 2026 vs July 2026"
 * @property {{currentPeriodEnd: string, previousPeriodEnd: string}} calendar
 *   - 'YYYY-MM-DD' boundaries. Consumed directly by transactions.js as
 *   filter cutoffs, and by releases.js as the upper bound for "most
 *   recent Release with forecast_date <= this boundary" — NOT as an
 *   assumption that forecast_date itself will land on these dates.
 */

/**
 * @typedef {Object} MetricValue
 * @property {number|null} current
 * @property {number|null} previous
 * @property {number|null} delta      - current - previous; null if either side is null
 * @property {number|null} deltaPct   - delta / previous * 100; null if previous is 0 or null
 *                                       (never silently divides by zero)
 * @property {{current: string|null, previous: string|null}} asOf
 *   - The actual effective date behind each value. For Transaction-based
 *   metrics this equals the calendar boundary that was filtered on. For
 *   Release-based metrics this is the persisted forecast_date of
 *   whichever Release was actually found — which may differ from the
 *   calendar boundary that was searched (e.g. searching "<= 2026-07-31"
 *   may resolve to a Release with forecast_date '2026-07-15'). This is
 *   part of the business result, not a UI-only detail: a consumer
 *   cannot correctly interpret current/previous without knowing what
 *   date they actually represent, so every consumer receives it
 *   directly rather than deriving or assuming it.
 */

function computeDelta(current, previous) {
  if (current == null || previous == null) {
    return { delta: null, deltaPct: null }
  }
  const delta = current - previous
  const deltaPct = previous !== 0 ? (delta / previous) * 100 : null
  return { delta, deltaPct }
}

function buildMetricValue(current, previous, asOfCurrent, asOfPrevious) {
  const { delta, deltaPct } = computeDelta(current, previous)
  return { current, previous, delta, deltaPct, asOf: { current: asOfCurrent, previous: asOfPrevious } }
}

/**
 * Public API. Given a project and a HistoricalContext, returns the full
 * set of historical KPI comparisons in one call.
 *
 * @param {string} projectId
 * @param {HistoricalContext} context
 */
export async function getHistoricalAnalytics(projectId, context) {
  if (context?.mode !== 'calendar') {
    throw new Error(
      `getHistoricalAnalytics: context.mode must be 'calendar' (only ` +
      `mode currently implemented). Received: ${context?.mode}`
    )
  }
  if (!projectId || !context?.calendar?.currentPeriodEnd || !context?.calendar?.previousPeriodEnd) {
    throw new Error(
      'getHistoricalAnalytics: projectId and a fully-formed context ' +
      '(context.calendar.currentPeriodEnd, context.calendar.previousPeriodEnd) ' +
      'are required. This layer does not resolve "today" or build the ' +
      'context itself — the caller must build it.'
    )
  }

  const [txHistory, releaseHistory] = await Promise.all([
    getTransactionHistory(projectId, context),
    getReleaseHistory(projectId, context),
  ])

  return {
    context: {
      mode: context.mode,
      label: context.label ?? null,
      calendar: {
        currentPeriodEnd: context.calendar.currentPeriodEnd,
        previousPeriodEnd: context.calendar.previousPeriodEnd,
      },
    },

    comparisonBasis: {
      transactions: 'transaction',
      releases: 'release',
    },

    dataQuality: {
      actual: txHistory.actual.quality,
      commitment: txHistory.commitment.quality,
      cashIn: txHistory.cashIn.quality,
      cashOut: txHistory.cashOut.quality,
      eac: releaseHistory.eac.quality,
      ev: releaseHistory.ev.quality,
      cpi: releaseHistory.cpi.quality,
    },

    metrics: {
      actual: buildMetricValue(txHistory.actual.current, txHistory.actual.previous, txHistory.actual.asOfCurrent, txHistory.actual.asOfPrevious),
      commitment: buildMetricValue(txHistory.commitment.current, txHistory.commitment.previous, txHistory.commitment.asOfCurrent, txHistory.commitment.asOfPrevious),
      cashIn: buildMetricValue(txHistory.cashIn.current, txHistory.cashIn.previous, txHistory.cashIn.asOfCurrent, txHistory.cashIn.asOfPrevious),
      cashOut: buildMetricValue(txHistory.cashOut.current, txHistory.cashOut.previous, txHistory.cashOut.asOfCurrent, txHistory.cashOut.asOfPrevious),
      eac: buildMetricValue(releaseHistory.eac.current, releaseHistory.eac.previous, releaseHistory.eac.asOfCurrent, releaseHistory.eac.asOfPrevious),
      ev: buildMetricValue(releaseHistory.ev.current, releaseHistory.ev.previous, releaseHistory.ev.asOfCurrent, releaseHistory.ev.asOfPrevious),
      cpi: buildMetricValue(releaseHistory.cpi.current, releaseHistory.cpi.previous, releaseHistory.cpi.asOfCurrent, releaseHistory.cpi.asOfPrevious),
    },
  }
}
